// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { NoteImage } from 'lib/dealhunt/types'
import { OwnerApiError } from 'lib/owner-notes/api'
import type { OwnerCredential } from 'lib/owner-notes/auth-bridge'
import { isSignInError, type KeyedUpload } from 'lib/owner-notes/keyed-api'
import {
  acceptFiles,
  parseStoredPhotos,
  photosFromImages,
  PHOTOS_COPY,
  PICK_AGAIN,
  REVIEW_PHOTOS_MAX,
  dropQueuedPhotos,
  reviewSkippedLine,
  serializePhotos,
  uploadReviewPhoto,
  uploadsToStart,
  type ReviewMedia,
  type ReviewPhoto,
} from 'lib/owner-notes/review-photos'

/** `sessionStorage`, never throwing: a blocked store just keeps no photos across a reload. */
function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}
function writeSession(key: string, value: string | null): void {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(key)
    } else {
      window.sessionStorage.setItem(key, value)
    }
  } catch {
    // Storage full or blocked: the stored set is a convenience.
  }
}

function newKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

/** How long after the sign-in dialog closes a session may still be arriving. */
const SIGN_IN_GRACE_MS = 1500

/** A session the proxy has not refused: an automatic retry never re-sends a refused token
 *  (Constraint 5599e215). */
function usable(cred: OwnerCredential | null, rejected: string | null): cred is OwnerCredential {
  return Boolean(cred && cred.token !== rejected)
}

export interface ReviewPhotosState {
  photos: ReviewPhoto[]
  /** Local previews (object URLs) for photos picked in this visit, by tile key. */
  previews: Readonly<Record<string, string>>
  /** The line after a pick that left files out. */
  notice: string | null
  room: number
  add: (files: File[]) => void
  remove: (key: string) => void
  retry: (key: string) => void
  /** The reader changed the set (a pick or a removal) since the band opened. */
  touched: boolean
  /** DealHunt has the note now: forget the tab's stored set. */
  forgetStored: () => void
  /** Mark these tiles failed ("Pick this photo again") — uploads past the 23 h window. */
  markExpired: (keys: readonly string[]) => void
}

/**
 * The band's photo set (contract-review-photos): tiles, uploads at pick time with the session's
 * bearer (≤ `REVIEW_PHOTO_SLOTS` at once), ✕ aborts, a refused token waits for a new one. Edit
 * mode starts from the note's `images`; a new review can keep its uploaded media ids in this
 * tab (`storageKey`, the community writer only), ≤ 23 h — the band's own slot, apart from the
 * host's photo-less `seed`. The band reads `photos` for `media_ids`; nothing here publishes.
 */
export function useReviewPhotos(args: {
  /** The note being edited's photos; null for a new review. */
  initial: NoteImage[] | null
  storageKey: string | null
  credential: OwnerCredential | null
  showLogIn: () => Promise<void>
  /** The sign-in dialog is open (`useOwnerAuth().loginOpen`): its close without a session drops
   *  the tiles that were waiting for one. */
  loginOpen?: boolean
  /** A gated create (the sign-in gate, 2026-10-17): a pick without a session is held queued —
   *  no dialog, never dropped — and uploads once the gate's sign-in brings the session. */
  holdForSession?: boolean
}): ReviewPhotosState {
  const {
    initial,
    storageKey,
    credential,
    showLogIn: openLogIn,
    loginOpen = false,
    holdForSession = false,
  } = args
  const editing = initial !== null
  const [photos, setPhotos] = useState<ReviewPhoto[]>(() => photosFromImages(initial ?? []))
  const [previews, setPreviews] = useState<Record<string, string>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)
  const files = useRef(new Map<string, File>())
  const uploads = useRef(new Map<string, KeyedUpload<ReviewMedia>>())
  const previewsRef = useRef(previews)
  previewsRef.current = previews
  const credentialRef = useRef(credential)
  credentialRef.current = credential
  const rejectedTokenRef = useRef<string | null>(null)
  const token = credential?.token ?? null
  // Stored-set writes start only after the restore read, so a mount never wipes it.
  const restoredRef = useRef(false)
  const keepStored = useRef(!editing && storageKey !== null)

  // A new review's stored set, after mount only (no storage during a server render).
  useEffect(() => {
    if (keepStored.current && storageKey) {
      const stored = parseStoredPhotos(readSession(storageKey), Date.now())
      if (stored.length) {
        setPhotos(stored)
      }
    }
    restoredRef.current = true
  }, [storageKey])

  useEffect(() => {
    if (restoredRef.current && keepStored.current && storageKey) {
      writeSession(storageKey, serializePhotos(photos))
    }
  }, [photos, storageKey])

  // Unmount: stop every upload and free the previews.
  useEffect(
    () => () => {
      uploads.current.forEach(u => u.abort())
      uploads.current.clear()
      Object.values(previewsRef.current).forEach(url => URL.revokeObjectURL(url))
    },
    []
  )

  // A sign-in this set asked for. `showLogIn` resolves when the dialog OPENS, so a dismissal is
  // the dialog closing with still no usable session: the queued tiles (never started — they were
  // waiting for the bearer) go, the line says "Sign in to add photos" again, and Publish is no
  // longer held by "Photos still uploading…". A grace period first: a successful sign-in closes
  // the dialog a moment before the session reaches this render.
  const askedRef = useRef(false)
  const showLogIn = useCallback((): Promise<void> => {
    askedRef.current = true
    return openLogIn()
  }, [openLogIn])
  const wasOpen = useRef(loginOpen)
  useEffect(() => {
    const closed = wasOpen.current && !loginOpen
    wasOpen.current = loginOpen
    if (!closed || !askedRef.current) {
      return undefined
    }
    const timer = window.setTimeout(() => {
      askedRef.current = false
      if (usable(credentialRef.current, rejectedTokenRef.current)) {
        return
      }
      setPhotos(list => {
        const { kept, dropped } = dropQueuedPhotos(list)
        dropped.forEach(key => {
          files.current.delete(key)
          const url = previewsRef.current[key]
          if (url) {
            URL.revokeObjectURL(url)
          }
        })
        return dropped.length ? kept : list
      })
      setNotice(PHOTOS_COPY.signIn)
    }, SIGN_IN_GRACE_MS)
    return () => window.clearTimeout(timer)
  }, [loginOpen])

  const update = useCallback((key: string, patch: Partial<ReviewPhoto>): void => {
    setPhotos(list => list.map(p => (p.key === key ? { ...p, ...patch } : p)))
  }, [])

  // The queue: queued tiles start once there is a usable session and a free slot.
  useEffect(() => {
    const cred = credentialRef.current
    if (!token || !usable(cred, rejectedTokenRef.current)) {
      return
    }
    const starts = uploadsToStart(photos, { usable: true, inFlight: uploads.current.size })
    for (const key of starts) {
      const file = files.current.get(key)
      if (!file) {
        update(key, { status: 'error', error: PICK_AGAIN })
        continue
      }
      update(key, { status: 'uploading', progress: 0, error: null })
      const upload = uploadReviewPhoto(cred, file, progress => update(key, { progress }))
      uploads.current.set(key, upload)
      upload.promise.then(
        media => {
          uploads.current.delete(key)
          update(key, {
            status: 'done',
            progress: 1,
            media_id: media.id,
            url: media.url,
            width: media.width,
            height: media.height,
            uploaded_at: Date.now(),
          })
        },
        (e: unknown) => {
          uploads.current.delete(key)
          if (e instanceof OwnerApiError && e.code === 'aborted') {
            return
          }
          if (isSignInError(e)) {
            rejectedTokenRef.current = cred.token
            update(key, { status: 'queued', progress: 0 })
            void showLogIn()
            return
          }
          update(key, {
            status: 'error',
            error: e instanceof OwnerApiError ? e.message : 'Upload failed',
          })
        }
      )
    }
  }, [photos, token, showLogIn, update])

  const room = Math.max(0, REVIEW_PHOTOS_MAX - photos.length)

  const add = (picked: File[]): void => {
    const { accepted, skipped } = acceptFiles(picked, room)
    setNotice(reviewSkippedLine(skipped) || null)
    if (!accepted.length) {
      return
    }
    const shown: Record<string, string> = {}
    const tiles: ReviewPhoto[] = accepted.map(file => {
      const key = newKey()
      files.current.set(key, file)
      shown[key] = URL.createObjectURL(file)
      return {
        key,
        status: 'queued',
        progress: 0,
        media_id: null,
        url: null,
        width: 0,
        height: 0,
        error: null,
        uploaded_at: null,
      }
    })
    setTouched(true)
    setPreviews(p => ({ ...p, ...shown }))
    setPhotos(list => [...list, ...tiles])
    if (!holdForSession && !usable(credentialRef.current, rejectedTokenRef.current)) {
      void showLogIn()
    }
  }

  const remove = (key: string): void => {
    uploads.current.get(key)?.abort()
    uploads.current.delete(key)
    files.current.delete(key)
    const url = previewsRef.current[key]
    if (url) {
      URL.revokeObjectURL(url)
      setPreviews(p => {
        const next = { ...p }
        delete next[key]
        return next
      })
    }
    setTouched(true)
    setNotice(null)
    setPhotos(list => list.filter(p => p.key !== key))
  }

  const retry = (key: string): void => {
    if (!files.current.has(key)) {
      // The file is gone (a reload): the tile cannot be sent again.
      remove(key)
      return
    }
    update(key, { status: 'queued', progress: 0, error: null })
    if (!holdForSession && !usable(credentialRef.current, rejectedTokenRef.current)) {
      void showLogIn()
    }
  }

  const forgetStored = useCallback((): void => {
    keepStored.current = false
    if (storageKey) {
      writeSession(storageKey, null)
    }
  }, [storageKey])

  const markExpired = useCallback((keys: readonly string[]): void => {
    const gone = new Set(keys)
    const fail = (p: ReviewPhoto): ReviewPhoto => {
      if (!gone.has(p.key)) {
        return p
      }
      return { ...p, status: 'error', media_id: null, error: PICK_AGAIN }
    }
    setPhotos(list => list.map(fail))
  }, [])

  return {
    photos,
    previews,
    notice,
    room,
    add,
    remove,
    retry,
    touched,
    forgetStored,
    markExpired,
  }
}
